import { useEffect, useMemo, useState } from 'react'
import { Antwortfehler, Betriebsstoerung, Nichtangemeldet, api, imHintergrund, Rechenabbruch } from './api'
import { anzahlwort, satz, t } from './sprache'
import { meldeErfolg } from './Erfolgsstelle'
import { useDarf } from './Rechte'
import type { Sprachschluessel } from './sprache'
import {
  SPIEGELARTEN,
  ZUSATZSPALTEN,
  ZUSCHNITT_AB_WERK,
  alsMonat,
  ausMonat,
  umgeschaltet,
  zuschnittLesen,
} from './Packagezuschnitt'
import type {
  Ebene,
  Packagevorlagenauftrag,
  Packagevorlagengrundlagen,
  Packagevorlagenvorschau,
  Packagezuschnitt,
} from './Packagezuschnitt'

/** Die Datei, die der Browser nach der Ortswahl hergibt — nur, was hier gebraucht wird. */
type Speicherziel = {
  name: string
  createWritable: () => Promise<{ write: (inhalt: Blob) => Promise<void>; close: () => Promise<void> }>
}

type Speicherwahl = (optionen: { suggestedName: string }) => Promise<Speicherziel>

/**
 * Was genau schiefging, als Satz für den Anwender (R145) — nie nur
 * „ließ sich nicht erzeugen".
 */
export function grund(f: unknown): string {
  if (f instanceof Rechenabbruch) {
    return satz(f.meldung) + (f.handreichung ? ' ' + satz(f.handreichung) : '')
  }
  if (f instanceof Antwortfehler) {
    if (f.status === 401) return t('packagevorlage.grund.abgemeldet')
    if (f.status === 404 || f.status === 403) return t('packagevorlage.grund.recht')
    return t('packagevorlage.grund.server')
  }
  if (f instanceof Betriebsstoerung) return t(f.schluessel)
  if (f instanceof Nichtangemeldet) return t('packagevorlage.grund.abgemeldet')
  // **Kein Satz des Browsers** (7.2 Abschnitt 36, R1140): Bis zum 2026-10-06
  // stand hier dessen eigene Meldung, oft englisch — „NotAllowedError: …".
  // Was beim Schreiben an den gewählten Ort scheitert, sagt ein eigener Satz.
  return f instanceof DOMException ? t('packagevorlage.grund.ort') : t('meldung.stoerung.unbekannt')
}

/** Ohne Ortswahl: in den Download-Ordner des Browsers. */
function herunterladen(name: string, inhalt: Blob) {
  const adresse = URL.createObjectURL(inhalt)
  try {
    const verweis = document.createElement('a')
    verweis.href = adresse
    verweis.download = name
    document.body.append(verweis)
    verweis.click()
    verweis.remove()
  } finally {
    URL.revokeObjectURL(adresse)
  }
}

function diesesMonat(): number {
  const heute = new Date()
  return heute.getFullYear() * 100 + heute.getMonth() + 1
}

/**
 * CoKo erzeugt die Package-Vorlage selbst (5.1 Abschnitt 3.37, R892–R897).
 *
 * **Sechs Fragen, jede vorbelegt.** Wer gleich „Erzeugen" drückt, bekommt
 * ein vollständiges Package; wer Knöpfe leer lässt, ein schlankes. Daneben
 * steht laufend, was der Lieferer in die Hand bekäme — die Blätter mit der
 * Zahl ihrer vorbelegten Zeilen, je Gesellschaft.
 *
 * **Erst „Erzeugen" schreibt** — die neue Datenquelle oder eine neue Fassung
 * ihres Zuschnitts (R892). Die Vorschau fragt nur.
 */
export function Packagevorlagen({ periode: gewaehltePeriode }: { periode: number }) {
  const darf = useDarf()
  // Ohne das Recht zur Vorschau wird nicht gefragt, und sie steht nicht da (R1180).
  const vorschauErlaubt = darf('packagevorlageVorschau')
  const [grundlagen, setGrundlagen] = useState<Packagevorlagengrundlagen | null>(null)
  const [periode, setPeriode] = useState(gewaehltePeriode > 0 ? gewaehltePeriode : diesesMonat())
  const [quelle, setQuelle] = useState<string | null>(null)
  const [neueQuelle, setNeueQuelle] = useState(t('packagevorlage.quelle.vorschlag'))
  const [gesellschaften, setGesellschaften] = useState<string[]>([])
  const [zuschnitt, setZuschnitt] = useState<Packagezuschnitt>(ZUSCHNITT_AB_WERK)
  const [vorschau, setVorschau] = useState<Packagevorlagenvorschau | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [vorschaufehler, setVorschaufehler] = useState<string | null>(null)

  // Die Periode der Auswahl oben kommt beim Start oft erst nach diesem
  // Bildschirm an — dann gilt sie, solange niemand hier eine andere gewählt hat.
  const [eigenePeriode, setEigenePeriode] = useState(false)
  useEffect(() => {
    if (gewaehltePeriode > 0 && !eigenePeriode) setPeriode(gewaehltePeriode)
  }, [gewaehltePeriode, eigenePeriode])

  useEffect(() => {
    void api.packagevorlagengrundlagen(periode).then((g) => {
      setGrundlagen(g)
      // **Alle Gesellschaften des Kreises vorbelegt**, und der Bestand
      // 01.01. nur, wenn eine von ihnen noch nie geliefert hat (R748).
      setGesellschaften((alt) => (alt.length > 0 ? alt : g.gesellschaften.map((x) => x.schluessel)))
      setZuschnitt((z) => ({ ...z, eroeffnung: g.gesellschaften.some((x) => x.erstesPackage) }))
    })
  }, [periode])

  const auftrag: Packagevorlagenauftrag = useMemo(
    () => ({
      quelle,
      neueQuelle: quelle ? null : neueQuelle.trim() || null,
      gesellschaften,
      periode,
      zuschnitt,
    }),
    [quelle, neueQuelle, gesellschaften, periode, zuschnitt],
  )

  // Die Vorschau folgt jeder Antwort — kurz verzögert, damit ein getippter
  // Name nicht für jeden Buchstaben fragt.
  useEffect(() => {
    if (!grundlagen || !vorschauErlaubt) return
    let gilt = true
    const wartezeit = setTimeout(() => {
      api.packagevorlageVorschau(auftrag).then(
        (v) => {
          if (!gilt) return
          setVorschau(v)
          setVorschaufehler(null)
        },
        // **Eine Vorschau, die nicht kommt, sagt es** — sonst stünde die der
        // vorigen Auswahl da, als gälte sie für diese.
        //
        // **Eine Störung sagt das Störungsfenster** (7.2 Abschnitt 30, R1003)
        // — hier stünde sie ein zweites Mal.
        (f: unknown) => {
          if (!gilt) return
          if (f instanceof Betriebsstoerung) {
            setVorschau(null)
            setVorschaufehler(null)
          } else {
            setVorschaufehler(t('packagevorlage.vorschau.fehler', { grund: grund(f) }))
          }
        },
      )
    }, 250)
    return () => {
      gilt = false
      clearTimeout(wartezeit)
    }
  }, [auftrag, grundlagen, vorschauErlaubt])

  function aendere(teil: Partial<Packagezuschnitt>) {
    setMeldung(null)
    setZuschnitt((z) => ({ ...z, ...teil }))
  }

  /** Eine vorhandene Datenquelle bringt ihre Antworten mit (R892). */
  function waehleQuelle(schluessel: string | null, meldungWeg = true) {
    if (meldungWeg) setMeldung(null)
    setQuelle(schluessel)
    const gemerkt = grundlagen?.quellen.find((q) => q.schluessel === schluessel)?.zuschnitt
    if (gemerkt) setZuschnitt(zuschnittLesen(gemerkt))
  }

  /**
   * Erst der Ort, dann die Datei.
   *
   * **Gefragt wird vor dem Erzeugen**: Wer die Abfrage abbricht, hat nichts
   * angelegt. Kann der Browser nicht fragen, landet die Datei in seinem
   * Download-Ordner — und die Meldung sagt genau das.
   */
  async function erzeugen() {
    setMeldung(null)
    const mappen = (vorschau?.mappen ?? []).filter((m) => !m.hinweis)
    const vorgeschlagen = mappen.length === 1 ? mappen[0]!.dateiname : `Reporting-Packages_${alsMonat(periode)}.zip`

    let ziel: Speicherziel | null = null
    const waehler = (window as unknown as { showSaveFilePicker?: Speicherwahl }).showSaveFilePicker
    if (typeof waehler === 'function') {
      try {
        ziel = await waehler({ suggestedName: vorgeschlagen })
      } catch (f) {
        // Abgebrochen heißt: nichts tun. Jeder andere Grund fällt auf den
        // Download-Ordner zurück, statt die Vorlage zu verweigern.
        if (f instanceof DOMException && f.name === 'AbortError') return
      }
    }

    setLaeuft(true)
    let erzeugt: { name: string; inhalt: Blob } | null = null
    try {
      erzeugt = await api.packagevorlageHolen(auftrag)
      if (ziel) {
        const strom = await ziel.createWritable()
        await strom.write(erzeugt.inhalt)
        await strom.close()
        // Was gelang, sagt das Kästchen oben rechts (7.2 Abschnitt 34, R1130).
        meldeErfolg(t('erfolg.packagevorlage.gespeichert'))
        setMeldung(null)
      } else {
        herunterladen(erzeugt.name, erzeugt.inhalt)
        meldeErfolg(t('erfolg.packagevorlage.heruntergeladen'))
        setMeldung(null)
      }
    } catch (f) {
      // Was beim Schreiben der Datei scheitert, steht hier; eine Störung des
      // Dienstes im Störungsfenster (7.2 Abschnitt 30, R1003).
      setMeldung(
        erzeugt
          ? t('packagevorlage.fehler.speichern', { datei: ziel?.name ?? erzeugt.name, grund: grund(f) })
          : f instanceof Betriebsstoerung
            ? null
            : t('packagevorlage.fehler', { grund: grund(f) }),
      )
    } finally {
      setLaeuft(false)
    }

    // Die neue Datenquelle steht ab jetzt in der Liste — auch nach einem
    // Fehler, wenn sie schon angelegt war — und ist gewählt, damit das
    // nächste Erzeugen keine zweite anlegt.
    // Ein Nachlesen im Hintergrund (R1007): still, mit einem zweiten Versuch.
    const neu = await imHintergrund(() => api.packagevorlagengrundlagen(periode)).catch(() => null)
    if (neu) {
      setGrundlagen(neu)
      if (!quelle) {
        const angelegt = neu.quellen.find((q) => !grundlagen?.quellen.some((a) => a.schluessel === q.schluessel))
        if (angelegt) waehleQuelle(angelegt.schluessel, false)
      }
    }
  }

  if (!grundlagen) {
    return (
      <section className="vorlagenbereich">
        <h1>{t('bereich.datenimport.packagevorlagen')}</h1>
      </section>
    )
  }

  const offeneFragen = vorschau?.stellvertreter ?? []
  const ebenen: Ebene[] = ['lokal', 'konzern', 'position']

  return (
    <section className="vorlagenbereich">
      <h1>{t('bereich.datenimport.packagevorlagen')}</h1>

      <div className="vorlagenraster">
        <div className="vorlagenfragen">
          <fieldset>
            <legend>{t('packagevorlage.frage.quelle')}</legend>
            <label>
              <input type="radio" name="quelle" checked={quelle === null} onChange={() => waehleQuelle(null)} />
              {t('packagevorlage.quelle.neu')}
            </label>
            {quelle === null && (
              <input
                aria-label={t('packagevorlage.quelle.name')}
                className="vorlagenname"
                value={neueQuelle}
                onChange={(e) => setNeueQuelle(e.target.value)}
              />
            )}
            {grundlagen.quellen.map((q) => (
              <label key={q.schluessel}>
                <input
                  type="radio"
                  name="quelle"
                  checked={quelle === q.schluessel}
                  onChange={() => waehleQuelle(q.schluessel)}
                />
                {q.name}
              </label>
            ))}
          </fieldset>

          <fieldset>
            <legend>{t('packagevorlage.frage.gesellschaften')}</legend>
            <label className="feld">
              <span>{t('kontext.periode')}</span>
              <input
                type="month"
                value={alsMonat(periode)}
                onChange={(e) => {
                  const p = ausMonat(e.target.value)
                  if (p > 0) {
                    setEigenePeriode(true)
                    setPeriode(p)
                  }
                }}
              />
            </label>
            {grundlagen.gesellschaften.map((g) => (
              <label key={g.schluessel}>
                <input
                  type="checkbox"
                  checked={gesellschaften.includes(g.schluessel)}
                  onChange={(e) =>
                    setGesellschaften((alt) =>
                      umgeschaltet(alt, g.schluessel, e.target.checked, grundlagen.gesellschaften.map((x) => x.schluessel)),
                    )
                  }
                />
                {g.name}
              </label>
            ))}
          </fieldset>

          <fieldset>
            <legend>{t('packagevorlage.frage.ebene')}</legend>
            {ebenen.map((ebene) => (
              <label key={ebene}>
                <input
                  type="radio"
                  name="ebene"
                  checked={zuschnitt.ebene === ebene}
                  onChange={() => aendere({ ebene })}
                />
                {t(`packagevorlage.ebene.${ebene}` as Sprachschluessel)}
              </label>
            ))}
            {zuschnitt.ebene === 'position' && offeneFragen.length > 0 && (
              // **Ein Formular und keine Tabelle**: je Position eine Frage
              // mit ihrer Auswahl — es gibt nichts zu sortieren oder zu ziehen.
              <div className="stellvertreter">
                <p>{t('packagevorlage.stellvertreter.frage')}</p>
                {offeneFragen.map((f) => (
                  <label key={f.position}>
                    <span>{f.name}</span>
                    <select
                      aria-label={f.name}
                      value={zuschnitt.stellvertreter[f.position] ?? ''}
                      onChange={(e) =>
                        aendere({ stellvertreter: { ...zuschnitt.stellvertreter, [f.position]: e.target.value } })
                      }
                    >
                      <option value="">{t('packagevorlage.stellvertreter.offen')}</option>
                      {f.konten.map((k) => (
                        // Die Nummer steht im Namen des Kontos — ein zweites
                        // Mal davor wäre die Kennung, und die liest kein Anwender.
                        <option key={k.schluessel} value={k.schluessel}>
                          {k.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <fieldset>
            <legend>{t('packagevorlage.frage.partner')}</legend>
            <label>
              <input type="radio" name="partner" checked={zuschnitt.partner} onChange={() => aendere({ partner: true })} />
              {t('packagevorlage.partner.ja')}
            </label>
            <label>
              <input type="radio" name="partner" checked={!zuschnitt.partner} onChange={() => aendere({ partner: false })} />
              {t('packagevorlage.partner.nein')}
            </label>
          </fieldset>

          <fieldset>
            <legend>{t('packagevorlage.frage.spiegel')}</legend>
            {SPIEGELARTEN.map((s) => (
              <label key={s}>
                <input
                  type="checkbox"
                  checked={zuschnitt.spiegel.includes(s)}
                  onChange={(e) => aendere({ spiegel: umgeschaltet(zuschnitt.spiegel, s, e.target.checked, SPIEGELARTEN) })}
                />
                {t(`packagevorlage.spiegel.${s}` as Sprachschluessel)}
              </label>
            ))}
          </fieldset>

          <fieldset>
            <legend>{t('packagevorlage.frage.zusaetze')}</legend>
            <label>
              <input
                type="checkbox"
                checked={zuschnitt.eroeffnung}
                onChange={(e) => aendere({ eroeffnung: e.target.checked })}
              />
              {t('packagevorlage.zusatz.eroeffnung')}
            </label>
            {ZUSATZSPALTEN.filter((d) => d !== 'kostenstelle' || grundlagen.kostenstelleGefuehrt).map((d) => (
              <label key={d}>
                <input
                  type="checkbox"
                  checked={zuschnitt.dimensionen.includes(d)}
                  onChange={(e) =>
                    aendere({ dimensionen: umgeschaltet(zuschnitt.dimensionen, d, e.target.checked, ZUSATZSPALTEN) })
                  }
                />
                {t(`packagevorlage.zusatz.${d}` as Sprachschluessel)}
              </label>
            ))}
            <label>
              <input type="radio" name="faktor" checked={zuschnitt.faktor === 1} onChange={() => aendere({ faktor: 1 })} />
              {t('packagevorlage.faktor.eins')}
            </label>
            <label>
              <input
                type="radio"
                name="faktor"
                checked={zuschnitt.faktor === 1000}
                onChange={() => aendere({ faktor: 1000 })}
              />
              {t('packagevorlage.faktor.tausend')}
            </label>
          </fieldset>
        </div>

        {/* Erzeugt wird aus der Vorschau heraus; ohne sie fehlt beides (R1180). */}
        {vorschauErlaubt && (
          <aside className="vorlagenvorschau" aria-label={t('packagevorlage.vorschau')}>
            <h2>{t('packagevorlage.vorschau')}</h2>
            {vorschau?.mappen.map((m) => (
              <div key={m.gesellschaft} className="vorlagenmappe">
                <h3>{m.name}</h3>
                {m.hinweis ? (
                  <p className="hinweis">{m.hinweis}</p>
                ) : (
                  <ul>
                    {m.blaetter.map((b) => (
                      <li key={b.blatt}>
                        {b.zeilen > 0
                          ? t('packagevorlage.blatt.zeilen', { blatt: b.blatt, zeilen: anzahlwort(b.zeilen) })
                          : b.blatt}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {vorschaufehler && <p className="vorlagenbefunde">{vorschaufehler}</p>}
            {vorschau && vorschau.befunde.length > 0 && (
              <ul className="vorlagenbefunde">
                {vorschau.befunde.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
            {darf('packagevorlageHolen') && (
              <button
                type="button"
                className="haupt"
                disabled={laeuft || !vorschau || vorschau.befunde.length > 0}
                onClick={() => void erzeugen()}
              >
                {t('packagevorlage.erzeugen')}
              </button>
            )}
            {meldung && <p role="status">{meldung}</p>}
          </aside>
        )}
      </div>
    </section>
  )
}
